<template>
  <span :style="wrapperStyle">
    <label v-if="fieldLabel" class="form-field-label">{{ fieldLabel }}</label>
    <div v-if="originalPreview && !drawingNew" class="signature-existing">
      <img :src="originalPreview" class="signature-preview" alt="Saved signature" />
      <button type="button" class="btn btn-sm btn-outline-secondary" @click="drawingNew = true">Draw a new signature</button>
    </div>
    <template v-else>
      <canvas
        ref="pad"
        class="signature-pad"
        width="480"
        height="160"
        aria-label="Signature"
        @pointerdown="startDraw"
        @pointermove="draw"
        @pointerup="finishStroke"
        @pointercancel="finishStroke"
      ></canvas>
      <div class="signature-actions">
        <button type="button" class="btn btn-sm btn-outline-secondary" @click="clearPad">Clear</button>
        <span v-if="stored" class="signature-ready">Signature ready. It is saved with the form.</span>
      </div>
    </template>
    <input type="hidden" :name="uniqueTitle" :value="stored" />
  </span>
</template>

<script>
import { inject, computed } from 'vue'
import { formattingToStyle } from '../../lib/client-rule-evaluator'
import { imageUploadFromDataUrl, imageDataUrl } from '../../../../shared/image-column'

export default {
  props: ['uniqueTitle', 'fieldLabel', 'description'],
  setup (props) {

    const formFormatting = inject('formFormatting', null)
    const formValues = inject('formValues', null)
    const updateFormValue = inject('updateFormValue', null)

    const wrapperStyle = computed(() => {
      if (!formFormatting || !props.uniqueTitle) return {}
      const fmt = formFormatting[props.uniqueTitle]
      return fmt ? formattingToStyle(fmt) : {}
    })

    return { formValues, updateFormValue, wrapperStyle }
  },
  data () {
    return {
      drawing: false,
      inked: false,
      stored: '',
      drawingNew: false,
      originalPreview: ''
    }
  },
  mounted () {
    this.showExisting()
    this.$nextTick(() => this.prepareCanvas())
  },
  watch: {
    drawingNew (open) {
      if (open) this.$nextTick(() => this.prepareCanvas())
    }
  },
  methods: {
    prepareCanvas () {
      const canvas = this.$refs.pad
      if (!canvas) return
      const context = canvas.getContext('2d')
      context.fillStyle = '#fff'
      context.fillRect(0, 0, canvas.width, canvas.height)
      context.strokeStyle = '#172018'
      context.lineWidth = 2
      context.lineCap = 'round'
    },
    point (event) {
      const rect = this.$refs.pad.getBoundingClientRect()
      return {
        x: (event.clientX - rect.left) * (this.$refs.pad.width / rect.width),
        y: (event.clientY - rect.top) * (this.$refs.pad.height / rect.height)
      }
    },
    startDraw (event) {
      const canvas = this.$refs.pad
      if (!canvas) return
      canvas.setPointerCapture(event.pointerId)
      this.drawing = true
      const context = canvas.getContext('2d')
      const point = this.point(event)
      context.beginPath()
      context.moveTo(point.x, point.y)
    },
    draw (event) {
      if (!this.drawing) return
      this.inked = true
      const point = this.point(event)
      const context = this.$refs.pad.getContext('2d')
      context.lineTo(point.x, point.y)
      context.stroke()
    },
    finishStroke () {
      const shouldStore = this.drawing && this.inked
      this.drawing = false
      if (shouldStore) this.saveDrawing()
    },
    clearPad () {
      this.inked = false
      this.stored = ''
      if (this.updateFormValue) this.updateFormValue(this.uniqueTitle, '')
      if (this.originalPreview) {
        this.drawingNew = false
        return
      }
      this.prepareCanvas()
    },
    saveDrawing () {
      if (!this.$refs.pad || !this.inked) return
      const upload = imageUploadFromDataUrl(this.$refs.pad.toDataURL('image/png'), 'signature.png', 'image/png')
      this.stored = JSON.stringify(upload)
      if (this.updateFormValue) this.updateFormValue(this.uniqueTitle, upload)
    },
    showExisting () {
      if (!this.formValues || !this.uniqueTitle) return
      const existing = imageDataUrl({ [this.uniqueTitle]: this.formValues[this.uniqueTitle], [`${this.uniqueTitle}_type`]: this.formValues[`${this.uniqueTitle}_type`] }, this.uniqueTitle)
      if (existing) this.originalPreview = existing
    }
  }
}
</script>

<style scoped>
.signature-pad {
  display: block;
  width: 100%;
  max-width: 480px;
  height: 160px;
  border: 1px solid #c8cdc4;
  border-radius: 4px;
  background: #fff;
  touch-action: none;
  cursor: crosshair;
}
.signature-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
}
.signature-ready {
  color: #3d4a34;
  font-size: 0.875rem;
}
.signature-existing {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
}
.signature-preview {
  display: block;
  max-width: 240px;
  max-height: 80px;
  border: 1px solid #c8cdc4;
  background: #fff;
}
</style>
